'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { AmbassadorContent } from './ambassador-content'

import { ThreadItem, ThreadSection } from '@/types/community-threads'

export interface BottomArticleModalData {
  readonly title: string
  readonly images?: readonly string[]
  readonly content?: string
}

export type BottomArticleItem = ThreadItem

export type AmbassadorBottomData = ThreadSection

const ArticleModal = ({
  article,
  open,
  onOpenChange,
}: {
  article: BottomArticleItem
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const contentData = article.modal?.content || ''
  const displayContent = Array.isArray(contentData)
    ? contentData.join('')
    : contentData

  const title = article.modal?.title || article.title
  const images = article.modal?.images || [article.image]

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalTrigger />
      <ResponsiveModalContent
        className='gap-0 border-none px-5 py-6 lg:max-w-[800px]'
        aria-describedby={'modal-article'}
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        <div className='no-scrollbar max-h-[80vh] space-y-6 overflow-y-auto px-1 pb-4'>
          {images && images.length > 0 && (
            <Carousel
              className='relative mb-3 w-full md:mb-6'
              opts={{ loop: true }}
            >
              <CarouselContent>
                {images.map((img: string, idx: number) => (
                  <CarouselItem key={idx}>
                    <Image
                      src={ImageHelper.getN7ImageUrl(img)}
                      alt={title}
                      width={700}
                      height={400}
                      className='aspect-[4/3] h-auto w-full rounded-[20px] object-cover md:aspect-[16/9]'
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselDots className='static translate-x-0 [&_.bg-primary]:!border-black [&_.bg-primary]:!bg-black' />
            </Carousel>
          )}

          <h3 className='mb-3 font-criteria text-2xl font-bold leading-tight text-neutral-900 md:mb-6 md:text-[32px]'>
            {title}
          </h3>

          {displayContent || article.seo_alias ? (
            <div className='flex flex-col gap-4 text-base leading-relaxed text-neutral-800'>
              <AmbassadorContent
                htmlContent={displayContent as string}
                disableApiFetch={!article.seo_alias}
                slug={article.seo_alias}
              />
            </div>
          ) : null}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

interface AmbassadorBottomProps {
  data: AmbassadorBottomData
}

export const AmbassadorBottom = ({ data }: AmbassadorBottomProps) => {
  const [selectedArticle, setSelectedArticle] =
    useState<BottomArticleItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeFilter, setActiveFilter] = useState<string>('')

  useEffect(() => {
    if (data?.show_filter && data?.filters?.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local UI state from server-provided data prop; unchanged pre-existing pattern
      setActiveFilter(data.filters[0].value)
    }
  }, [data?.show_filter, data?.filters])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- React Compiler isn't enabled in this project; keeping the existing explicit deps to avoid changing recompute behavior
  const filteredItems = useMemo(() => {
    if (!data?.items) return []
    if (!data.show_filter || !activeFilter || activeFilter === 'all') {
      return data.items
    }
    return data.items.filter((item) => item.tag === activeFilter)
  }, [data?.items, data?.show_filter, activeFilter])

  if (!data?.items?.length) return null

  return (
    <div>
      <div
        className={cn(
          'mb-4 flex flex-col justify-between md:mb-8 md:flex-row md:items-center',
          data?.show_filter && 'gap-4'
        )}
      >
        <h3 className='font-criteria text-2xl font-medium uppercase text-[#111111] md:text-[28px]'>
          {data.blog_title}
        </h3>
        <div className='flex items-center gap-4'>
          {data.show_filter && (
            <div className='flex items-center gap-2'>
              {data.filters?.map((filter, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveFilter(filter.value)}
                  className={cn(
                    'rounded-full px-5 py-2 text-sm font-semibold transition-colors',
                    activeFilter === filter.value
                      ? 'bg-[#111] text-white'
                      : 'bg-[#E5E5E5] text-black hover:bg-[#D5D5D5]'
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Carousel
        opts={{
          align: 'start',
          loop: false,
        }}
        className='w-full'
      >
        <CarouselContent className='-ml-4'>
          {filteredItems.map((item: BottomArticleItem, idx: number) => (
            <CarouselItem
              key={idx}
              className={cn(
                'basis-[83.333%] pl-4 md:basis-1/2 lg:basis-1/3',
                !item.modal && 'cursor-default'
              )}
            >
              <div
                className='group relative aspect-[587/360] w-full cursor-pointer overflow-hidden rounded-2xl bg-gray-200'
                onClick={() => {
                  if (item.modal || item.seo_alias) {
                    setSelectedArticle(item)
                    setIsModalOpen(true)
                  }
                }}
              >
                <Image
                  src={ImageHelper.getN7ImageUrl(item.image)}
                  alt={item.title}
                  width={700}
                  height={400}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                />
                <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/40 to-transparent p-6 pt-24'>
                  <h4 className='font-criteria text-base font-bold text-white md:text-xl'>
                    {item.title}
                  </h4>
                </div>
                {/* {item.tag && (
                  <div className='absolute left-4 top-4 rounded-full bg-[#D9FF00] px-3 py-1 text-xs font-bold text-black'>
                    {item.tag}
                  </div>
                )} */}
                {(item.modal || item.seo_alias) && (
                  <div className='absolute bottom-6 right-6 flex h-8 w-8 items-center justify-center rounded-full bg-black/80 text-white opacity-0 transition-opacity group-hover:opacity-100'>
                    →
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          hideIfStart
          variant='black'
          className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2'
        />
        <CarouselNext
          hideIfEnd
          variant='black'
          className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2'
        />
      </Carousel>

      {isModalOpen && selectedArticle && (
        <ArticleModal
          article={selectedArticle}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </div>
  )
}
